import { type FC, useMemo } from "react";

import { ChartWrapper, HorizontalBarChart, NoData } from "../../../../../components/charts";
import { RequestStatusRenderer } from "../../../../../components/requestStatsRenderer/RequestStatusRenderer";

import "./packPurchasedPerformance.scss";
import { reportingColors } from "features/Library/Common/utils/constants";
import { unlimitedNumber } from "utils/miscellaneousUtils";
import { sharedAccountReportingHorizontalBarProps } from "features/Reporting/Content/shared";
import { useCustomerAssignedLicensesQuery, useCustomerDepartmentUsageQuery } from "../../queries";
import { type PerformanceFilter } from "features/Library/Common/utils/performanceUtils";

export interface PackPurchasedPerformanceProps {
  packId: number;
  filter: PerformanceFilter;
  moboId?: number;
}

export const PackPurchasedPerformance: FC<PackPurchasedPerformanceProps> = ({ packId, moboId, filter }) => {
  const customerAssignedLicensesQuery = useCustomerAssignedLicensesQuery({
    packId,
    filter,
    customerId: moboId,
    enabled: !!packId,
  });

  const customerDepartmentUsageQuery = useCustomerDepartmentUsageQuery({
    packId,
    filter,
    customerId: moboId,
    enabled: !!packId,
  });

  const accountLicensePieData = useMemo(() => {
    return customerAssignedLicensesQuery.data?.chartData || [];
  }, [customerAssignedLicensesQuery.data?.chartData]);

  const licenseDepartmentData = useMemo(() => {
    return customerDepartmentUsageQuery.data || [];
  }, [customerDepartmentUsageQuery.data]);

  const noAccountLicenseData = useMemo<boolean>(
    () =>
      customerAssignedLicensesQuery.status === "success" && accountLicensePieData && accountLicensePieData.length === 0,
    [customerAssignedLicensesQuery.status, accountLicensePieData],
  );

  const noLicenseUsageData = useMemo<boolean>(
    () =>
      customerDepartmentUsageQuery.status === "success" && licenseDepartmentData && licenseDepartmentData.length === 0,
    [customerDepartmentUsageQuery.status, licenseDepartmentData],
  );

  const isOwnPack = useMemo(
    () => customerAssignedLicensesQuery.data?.raw.TotalLicenses === unlimitedNumber,
    [customerAssignedLicensesQuery.data?.raw.TotalLicenses],
  );

  return (
    <div className="packPurchasedPerformanceRoot">
      <header className="packPurchasedHeader">
        <h2 className="packPurchasedTitle">Summary</h2>
      </header>
      <div className="firstRow">
        <div className="activeLicenseChart">
          <ChartWrapper
            titles={["Active Licenses"]}
            showLegend
            legendLabels={
              isOwnPack || customerAssignedLicensesQuery.status !== "success" ? ["Issued"] : ["Issued", "Remaining"]
            }
            colorRange={
              isOwnPack || customerAssignedLicensesQuery.status !== "success"
                ? [reportingColors.blue]
                : [reportingColors.blue, reportingColors.yellow]
            }
          >
            <RequestStatusRenderer state={customerAssignedLicensesQuery.status}>
              {noAccountLicenseData ? (
                <NoData />
              ) : (
                <>
                  {isOwnPack ? (
                    <HorizontalBarChart
                      {...sharedAccountReportingHorizontalBarProps}
                      data={[accountLicensePieData[0]]}
                    />
                  ) : (
                    <HorizontalBarChart
                      {...sharedAccountReportingHorizontalBarProps}
                      data={accountLicensePieData}
                      countBarsToShowAtOnce={2}
                    />
                  )}
                </>
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
        <div className="licenseUsageChart">
          <ChartWrapper titles={["License Usage - Department"]}>
            <RequestStatusRenderer state={customerDepartmentUsageQuery.status}>
              {noLicenseUsageData ? (
                <NoData />
              ) : (
                <HorizontalBarChart
                  {...sharedAccountReportingHorizontalBarProps}
                  data={licenseDepartmentData}
                  countBarsToShowAtOnce={7}
                />
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
      </div>
    </div>
  );
};

export default PackPurchasedPerformance;
